import { useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n';

export const ForgotPasswordDialog = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const response = await authAPI.forgotPassword({ email });
      toast.success(response.data?.message || t('passwordReset.requestSuccess'));
      setEmail('');
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      const isProviderRestriction = typeof detail === 'string' && (
        detail.includes('testing emails') || detail.includes('verify a domain')
      );
      toast.error(isProviderRestriction ? t('passwordReset.providerRestriction') : (detail || t('passwordReset.requestError')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>{t('passwordReset.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('passwordReset.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit} data-testid="forgot-password-form">
          <div className="space-y-2">
            <Label htmlFor="forgot-password-email">{t('labels.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="forgot-password-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('passwordReset.emailPlaceholder')}
                className="pl-10"
                required
                data-testid="forgot-password-email-input"
              />
            </div>
          </div>

          <DialogFooter className="sm:justify-end">
            <Button type="submit" disabled={submitting} data-testid="forgot-password-submit-button">
              {submitting ? (
                <span>{t('passwordReset.sendingLink')}</span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  {t('passwordReset.sendLink')}
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};